import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, wsToken, API } from "@/lib/api";
import { confirmDelete } from "@/lib/confirm";
import { useWs } from "@/context/WorkspaceContext";
import { PageHeader, EmptyState, fmtDate } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { FolderPlus, Upload, Folder, FileText, FileImage, Trash2, Download, ChevronRight, Loader2 } from "lucide-react";

function fileIcon(ct) {
  if (ct?.startsWith("image/")) return FileImage;
  return FileText;
}

function humanSize(bytes) {
  if (!bytes) return "";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0, n = bytes;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function Files() {
  const { canEdit } = useWs();
  const writable = canEdit;
  const canUpload = canEdit;
  const [current, setCurrent] = useState(null);
  const [data, setData] = useState({ folders: [], files: [], breadcrumb: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/folders", { params: { parent_id: current } });
      setData(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current]);

  const createFolder = async () => {
    try {
      await api.post("/folders", { name: folderName, parent_id: current });
      toast.success("Folder created");
      setOpen(false);
      setFolderName("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (current) fd.append("folder_id", current);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeFolder = async (id, e) => {
    e.stopPropagation();
    if (!(await confirmDelete({ message: "Delete this folder and its contents?" }))) return;
    await api.delete(`/folders/${id}`);
    toast.success("Folder deleted");
    load();
  };

  const removeFile = async (id, e) => {
    e.stopPropagation();
    if (!(await confirmDelete({ message: "Delete this file?" }))) return;
    await api.delete(`/files/${id}`);
    toast.success("File deleted");
    load();
  };

  const openFile = (f) => {
    const url = `${API}/files/${f.id}/download?auth=${wsToken()}`;
    window.open(url, "_blank");
  };

  const isEmpty = data.folders.length === 0 && data.files.length === 0;

  return (
    <div>
      <PageHeader
        title="Files"
        subtitle="Important client documents and assets, organised into folders."
        action={
          (writable || canUpload) && (
            <div className="flex gap-2">
              {writable && (
                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-folder-button" variant="outline" className="border-[#EEEFEF] bg-transparent text-[#595D62] hover:bg-[#F6F6FD] hover:text-[#06060C]">
                      <FolderPlus className="h-4 w-4 mr-1.5" /> New folder
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white border-[#EEEFEF] text-[#06060C]">
                    <DialogHeader><DialogTitle className="font-display">New folder</DialogTitle></DialogHeader>
                    <div className="py-2">
                      <label className="text-xs text-[#9FA1A4] mb-1.5 block font-mono-data uppercase tracking-wider">Folder name</label>
                      <Input data-testid="folder-name-input" value={folderName} onChange={(e) => setFolderName(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF]" placeholder="Contracts" />
                    </div>
                    <DialogFooter>
                      <Button data-testid="folder-submit-button" onClick={createFolder} disabled={!folderName} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">Create</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              )}
              {canUpload && (
                <>
                  <input ref={fileRef} type="file" hidden onChange={upload} data-testid="file-input" />
                  <Button data-testid="upload-file-button" onClick={() => fileRef.current?.click()} disabled={uploading} className="bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload
                  </Button>
                </>
              )}
            </div>
          )
        }
      />

      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 mb-6 text-sm">
        <button data-testid="files-root-crumb" onClick={() => setCurrent(null)} className={`${current ? "text-[#9FA1A4] hover:text-[#06060C]" : "text-[#06060C]"} transition-colors`}>Home</button>
        {data.breadcrumb.map((b) => (
          <React.Fragment key={b.id}>
            <ChevronRight className="h-3.5 w-3.5 text-[#c4c5cc]" />
            <button onClick={() => setCurrent(b.id)} className="text-[#595D62] hover:text-[#06060C] transition-colors">{b.name}</button>
          </React.Fragment>
        ))}
      </div>

      {loading ? (
        <div className="text-[#9FA1A4] text-sm">Loading…</div>
      ) : isEmpty ? (
        <EmptyState icon={Folder} title="This folder is empty" description={writable ? "Create a folder or upload files to get started." : "Upload a file to get started."} testid="files-empty" />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {data.folders.map((f) => (
            <div key={f.id} data-testid="folder-card" onClick={() => setCurrent(f.id)} className="group cursor-pointer rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-5 hover:-translate-y-1 hover:border-[#d3d4da] transition-all duration-300">
              <div className="flex items-start justify-between">
                <Folder className="h-8 w-8 text-[#723FFF]" />
                {writable && <button data-testid="folder-delete-button" onClick={(e) => removeFolder(f.id, e)} className="opacity-0 group-hover:opacity-100 text-[#c4c5cc] hover:text-rose-600 transition-all"><Trash2 className="h-4 w-4" /></button>}
              </div>
              <p className="mt-4 font-medium text-[#06060C] truncate">{f.name}</p>
              <p className="font-mono-data text-[10px] text-[#9FA1A4] mt-0.5">Folder</p>
            </div>
          ))}
          {data.files.map((f) => {
            const Icon = fileIcon(f.content_type);
            return (
              <div key={f.id} data-testid="file-card" onClick={() => openFile(f)} className="group cursor-pointer rounded-3xl border border-[#EEEFEF] premium-shadow bg-white p-5 hover:-translate-y-1 hover:border-[#d3d4da] transition-all duration-300">
                <div className="flex items-start justify-between">
                  <Icon className="h-8 w-8 text-[#9FA1A4]" />
                  <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-all">
                    <button onClick={(e) => { e.stopPropagation(); openFile(f); }} className="text-[#c4c5cc] hover:text-[#06060C]"><Download className="h-4 w-4" /></button>
                    {writable && <button data-testid="file-delete-button" onClick={(e) => removeFile(f.id, e)} className="text-[#c4c5cc] hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </div>
                <p className="mt-4 font-medium text-[#06060C] truncate" title={f.original_filename}>{f.original_filename}</p>
                <p className="font-mono-data text-[10px] text-[#9FA1A4] mt-0.5">{humanSize(f.size)} • {fmtDate(f.created_at)}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
